import { createI18n } from 'vue-i18n';
import { watch } from 'vue';
import de from '../i18n/de.ts';
import en from '../i18n/en.ts';

export const LOCALES = ['de', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

const STORAGE_KEY = 'stadtpuls.locale';

/**
 * The language to start in: the one the operator chose last time, else German.
 *
 * German rather than the browser's preference, because the audience is a German
 * control room and the choice is one click away in the app bar. The stored
 * choice is a per-browser convenience, so storage failing (a private window,
 * blocked site data) must degrade to the default rather than break the page,
 * which is why every access is wrapped.
 */
function initialLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if ((LOCALES as readonly string[]).includes(stored ?? '')) return stored as Locale;
  } catch {
    // Storage unavailable. The default is fine.
  }
  return 'de';
}

/**
 * Composition-API mode (`legacy: false`), which vue-i18n 11 recommends and which
 * the rest of this app is written in. The legacy mode exists for Options API
 * components, and the one place that idiom lives on purpose is the
 * legacy-compat module from ADR-05, which brings its own.
 */
export const i18n = createI18n({
  legacy: false,
  locale: initialLocale(),
  fallbackLocale: 'en',
  messages: { de, en },
});

export function setLocale(locale: Locale): void {
  i18n.global.locale.value = locale;
}

// Keep the document's language in step, which screen readers use to pick a
// voice, and remember the choice.
watch(
  i18n.global.locale,
  (locale) => {
    document.documentElement.lang = locale;
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      // A private window. The choice lasts for this page only.
    }
  },
  { immediate: true },
);
